import React, { useState, useRef, useEffect } from 'react';
import SignaturePad from 'signature_pad';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/Dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/Tabs';
import { Loader2 } from 'lucide-react';

interface SignatureModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: {
    signatureType: 'draw' | 'type';
    signatureImage?: string;
    typedName?: string;
    signingDate: string;
    consentGiven: boolean;
    otpCode?: string;
  }) => Promise<void>;
  showOtp?: boolean;
  onSendOtp?: () => Promise<void>;
  onVerifyOtp?: (code: string) => Promise<void>;
  otpVerified?: boolean;
  otpLoading?: boolean;
  loading?: boolean;
  stepLabel?: string; // Dynamic step label (e.g., "Step 1 of 2: Client Signing")
}

export const SignatureModal: React.FC<SignatureModalProps> = ({
  open,
  onClose,
  onSubmit,
  showOtp = false,
  onSendOtp,
  onVerifyOtp,
  otpVerified = false,
  otpLoading = false,
  loading = false,
  stepLabel,
}) => {
  const [signatureMode, setSignatureMode] = useState<'draw' | 'type'>('draw');
  const [typedName, setTypedName] = useState<string>('');
  const [signingDate, setSigningDate] = useState<string>(() => {
    // Default to today's date in YYYY-MM-DD format
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [consentGiven, setConsentGiven] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const signaturePadRef = useRef<SignaturePad | null>(null);
  const [isEmpty, setIsEmpty] = useState(true);

  // Initialize signature pad
  useEffect(() => {
    if (open && signatureMode === 'draw') {
      let resizeObserver: ResizeObserver | null = null;
      let handleBegin: (() => void) | null = null;
      let resizeCanvas: (() => void) | null = null;
      let initTimeout: NodeJS.Timeout | null = null;
      let retryCount = 0;
      const maxRetries = 10;

      const initializeSignaturePad = () => {
        const canvas = canvasRef.current;
        if (!canvas) {
          // Retry if canvas is not available yet (might be hidden by TabsContent)
          if (retryCount < maxRetries) {
            retryCount++;
            initTimeout = setTimeout(initializeSignaturePad, 50);
          }
          return;
        }

        // Check if canvas has proper dimensions
        const rect = canvas.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) {
          // Canvas not yet visible/sized, retry
          if (retryCount < maxRetries) {
            retryCount++;
            initTimeout = setTimeout(initializeSignaturePad, 50);
          }
          return;
        }

        // Set canvas size with high-DPI support
        const ratio = Math.max(window.devicePixelRatio || 1, 1);
        canvas.width = rect.width * ratio;
        canvas.height = rect.height * ratio;

        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.scale(ratio, ratio);
        }

        // Initialize SignaturePad with proper options
        const signaturePad = new SignaturePad(canvas, {
          backgroundColor: 'rgb(255, 255, 255)',
          penColor: 'rgb(0, 0, 0)',
          throttle: 0, // Disable throttling for better responsiveness
          minWidth: 0.5,
          maxWidth: 2.5,
          velocityFilterWeight: 0.7,
        });

        signaturePadRef.current = signaturePad;

        handleBegin = () => {
          setIsEmpty(false);
        };

        signaturePad.addEventListener('beginStroke', handleBegin);

        // Handle canvas resize
        resizeCanvas = () => {
          if (canvas && signaturePadRef.current) {
            const ratio = Math.max(window.devicePixelRatio || 1, 1);
            const rect = canvas.getBoundingClientRect();
            
            // Only resize if canvas has dimensions
            if (rect.width > 0 && rect.height > 0) {
              const currentData = signaturePadRef.current.toData();

              canvas.width = rect.width * ratio;
              canvas.height = rect.height * ratio;

              const context = canvas.getContext('2d');
              if (context) {
                context.scale(ratio, ratio);
              }

              signaturePadRef.current.clear();
              signaturePadRef.current.fromData(currentData);
            }
          }
        };

        // Use ResizeObserver for better resize detection
        resizeObserver = new ResizeObserver(resizeCanvas);
        resizeObserver.observe(canvas);

        window.addEventListener('resize', resizeCanvas);
      };

      // Start initialization with a small delay to ensure DOM is ready
      initTimeout = setTimeout(initializeSignaturePad, 100);

      return () => {
        if (initTimeout) {
          clearTimeout(initTimeout);
        }
        if (resizeObserver) {
          resizeObserver.disconnect();
        }
        if (resizeCanvas) {
          window.removeEventListener('resize', resizeCanvas);
        }
        if (signaturePadRef.current && handleBegin) {
          signaturePadRef.current.removeEventListener('beginStroke', handleBegin);
        }
        if (signaturePadRef.current) {
          signaturePadRef.current.clear();
          signaturePadRef.current = null;
        }
      };
    } else {
      // Clean up when switching modes or closing
      if (signaturePadRef.current) {
        signaturePadRef.current.clear();
        signaturePadRef.current = null;
      }
    }
  }, [open, signatureMode]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setSignatureMode('draw');
      setTypedName('');
      setConsentGiven(false);
      setOtpCode('');
      setOtpSent(false);
      setIsEmpty(true);
      if (signaturePadRef.current) {
        signaturePadRef.current.clear();
      }
    }
  }, [open]);

  const handleClear = () => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
      setIsEmpty(true);
    }
  };

  const handleSendOtp = async () => {
    if (onSendOtp) {
      try {
        await onSendOtp();
        setOtpSent(true);
      } catch (err) {
        console.error('Error sending OTP:', err);
      }
    }
  };

  const handleVerifyOtp = async () => {
    if (onVerifyOtp && otpCode) {
      try {
        await onVerifyOtp(otpCode);
      } catch (err) {
        console.error('Error verifying OTP:', err);
        alert('Invalid OTP code. Please try again.');
      }
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (signatureMode === 'draw' && isEmpty) {
      alert('Please draw your signature or switch to type mode');
      return;
    }

    if (signatureMode === 'type' && !typedName.trim()) {
      alert('Please enter your name');
      return;
    }

    if (!consentGiven) {
      alert('Please agree to sign electronically');
      return;
    }

    // Validate signing date
    const selectedDate = new Date(signingDate);
    const today = new Date();
    today.setHours(23, 59, 59, 999); // End of today
    if (selectedDate > today) {
      alert('Signing date cannot be in the future');
      return;
    }

    // Get signature image if draw mode
    let finalSignatureImage: string | undefined;
    if (signatureMode === 'draw' && signaturePadRef.current && !signaturePadRef.current.isEmpty()) {
      finalSignatureImage = signaturePadRef.current.toDataURL('image/png');
    }

    await onSubmit({
      signatureType: signatureMode,
      signatureImage: finalSignatureImage,
      typedName: signatureMode === 'type' ? typedName.trim() : undefined,
      signingDate,
      consentGiven,
      otpCode: showOtp && otpVerified ? otpCode : undefined,
    });
  };

  const canSubmit =
    (signatureMode === 'draw' && !isEmpty) ||
    (signatureMode === 'type' && typedName.trim().length >= 2);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Sign Document</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Step Indicator */}
          {stepLabel && (
            <div className="text-sm text-gray-600 text-center">
              {stepLabel}
            </div>
          )}

          {/* Signature Mode Toggle */}
          <Tabs value={signatureMode} onValueChange={(v) => setSignatureMode(v as 'draw' | 'type')}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="draw">Draw</TabsTrigger>
              <TabsTrigger value="type">Type Name</TabsTrigger>
            </TabsList>

            <TabsContent value="draw" className="space-y-4">
              <div 
                className="border-2 border-dashed border-gray-300 rounded-lg p-4 bg-white"
                style={{
                  touchAction: 'none',
                  pointerEvents: 'auto',
                }}
              >
                <canvas
                  ref={canvasRef}
                  className="w-full h-48 cursor-crosshair"
                  style={{
                    touchAction: 'none',
                    pointerEvents: 'auto',
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    display: 'block',
                  }}
                />
              </div>
              <div className="flex justify-between">
                <Button type="button" variant="outline" onClick={handleClear}>
                  Clear
                </Button>
                <p className="text-xs text-gray-500 self-center">
                  Sign using your finger, stylus, or mouse
                </p>
              </div>
            </TabsContent>

            <TabsContent value="type" className="space-y-4">
              <Input
                label="Type Your Full Name"
                value={typedName}
                onChange={(e) => setTypedName(e.target.value)}
                placeholder="Enter your full name"
                required
                className="text-lg font-cursive"
                style={{ fontFamily: 'cursive, serif' }}
              />
              {typedName.length > 0 && typedName.length < 2 && (
                <p className="text-sm text-error">Name must be at least 2 characters</p>
              )}
            </TabsContent>
          </Tabs>

          {/* Signing Date Picker */}
          <div className="space-y-2">
            <Input
              label="Signing Date"
              type="date"
              value={signingDate}
              onChange={(e) => setSigningDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              helperText="This date will appear on the signed document"
              required
            />
          </div>

          {/* OTP Flow (if enabled) */}
          {showOtp && (
            <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
              <div className="text-sm font-medium">Email Verification</div>
              {!otpSent ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleSendOtp}
                  disabled={otpLoading}
                >
                  {otpLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send Verification Code'
                  )}
                </Button>
              ) : (
                <div className="space-y-2">
                  <Input
                    label="Enter Verification Code"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value)}
                    placeholder="6-digit code"
                    maxLength={6}
                  />
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleVerifyOtp}
                      disabled={otpCode.length !== 6 || otpLoading}
                    >
                      {otpLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Verifying...
                        </>
                      ) : (
                        'Verify Code'
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setOtpSent(false);
                        setOtpCode('');
                      }}
                    >
                      Resend
                    </Button>
                  </div>
                  {otpVerified && (
                    <p className="text-sm text-green-600">✓ Code verified successfully</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Consent Checkbox */}
          <div className="flex items-start space-x-2">
            <Checkbox
              id="consent"
              checked={consentGiven}
              onCheckedChange={(checked) => setConsentGiven(checked === true)}
              required
            />
            <label
              htmlFor="consent"
              className="text-sm leading-relaxed cursor-pointer"
            >
              I agree to sign this document electronically and acknowledge that my electronic signature
              has the same legal effect as a handwritten signature.
            </label>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!canSubmit || !consentGiven || (showOtp && !otpVerified) || loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Sign Document'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
